import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { getSpanishIdValidationMessage, normalizeId } from './spanish-id.utils';

const TIME_PAD_LENGTH = 2;

export class FormCustomValidators {

  /**
   * Time validator (24h, format HH:mm).
   * Returns null when empty (let required() handle emptiness).
   */
  get timeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!(Boolean(value))) {return null;} // allow empty

      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      let normalizedValue = String(value).trim();

      if (value instanceof Date) {
        normalizedValue = this.formatTimeValue(value);
      } else if (typeof value === 'string' && !timePattern.test(normalizedValue)) {
        const parsedDate = new Date(normalizedValue);

        if (!Number.isNaN(parsedDate.getTime())) {
          normalizedValue = this.formatTimeValue(parsedDate);
        }
      }

      if (!timePattern.test(normalizedValue) || normalizedValue.startsWith('-')) {
        return { invalidTime: 'no es un tiempo válido (HH:mm) o es negativo.' };
      }
      return null;
    };
  } // ();

  private formatTimeValue(value: Date): string {
    const hours = value.getHours().toString().padStart(TIME_PAD_LENGTH, '0');
    const minutes = value.getMinutes().toString().padStart(TIME_PAD_LENGTH, '0');

    return `${hours}:${minutes}`;
  }

  /**
   * Spanish ID validator.
   * Accepts NIF (incl. K/L/M), NIE, and CIF — validates control char.
   * Returns null when empty (let required() handle it).
   */
  get spanishIdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = normalizeId(control.value ?? '');

      if (!raw) {return null;}

      const validationMessage = getSpanishIdValidationMessage(raw);

      return validationMessage === null ? null : { invalidSpanishId: validationMessage };
    };
  } // ();

  /**
   * Builds a regex validator with a user-friendly error message.
   */
  public createPatternMessageValidator(pattern: RegExp | string, message: string): ValidatorFn {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();

      if (value.length === 0) {
        return null;
      }

      regex.lastIndex = 0;

      return regex.test(value) ? null : { invalidPattern: message };
    };
  } // createPatternMessageValidator();

  /**
   * Requires the platform password when the field is visible in the reactive form.
   */
  public createPasswordRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = String(control.value ?? '').trim();

      return password.length > 0
        ? null
        : { passwordRequired: 'La contraseña es obligatoria para los trabajadores con acceso a la plataforma.' };
    };
  } // createPasswordRequiredValidator();

  /**
   * Validates that the repeated password is present and matches the main password.
   */
  public createRepeatedPasswordValidator(passwordField = 'password'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = String(control.parent?.get(passwordField)?.value ?? '').trim();
      const repeatedPassword = String(control.value ?? '').trim();

      if (password.length === 0 && repeatedPassword.length === 0) {
        return null;
      }

      if (password.length > 0 && repeatedPassword.length === 0) {
        return { passwordRepeatRequired: 'Por favor, repita la contraseña.' };
      }

      return password === repeatedPassword ? null : { passwordMismatch: 'Las contraseñas no coinciden.' };
    };
  } // createRepeatedPasswordValidator();

  /**
   * Duplicate Spanish ID validator (factory).
   * Provide an existence predicate that knows how to check your dataset.
   *
   * @param existsFn Function that returns true if the (normalized) ID is already taken.
   *                 It should ignore the "current record" when editing (if applicable).
   *
   * Example usage:
   *   const existsFn = (id: string) =>
   *     (this.lists.clientes.values ?? []).some(opt =>
   *       spanishIdEquals((opt?.value as any)?.nif ?? '', id) &&
   *       (opt?.value as any)?.id !== this.model?.id);
   *   this.dupSpanishIdValidator = new FormCustomValidators().createDuplicateSpanishIdValidator(existsFn);
   */
  createDuplicateValueValidator(existsFn: (normalizedValue: string) => boolean, message = 'ya existe en otro registro.'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control.value ?? '').trim();

      if (!raw) {return null;}

      const normalizedValue = normalizeId(raw);

      return existsFn(normalizedValue) || existsFn(raw) ? { duplicateValue: message } : null;
    };
  } // createDuplicateValueValidator();

  createDuplicateSpanishIdValidator(existsFn: (normalizedId: string) => boolean): ValidatorFn {
    return this.createDuplicateValueValidator(existsFn, 'ya existente.');
  } // ();
} // class;
